import { useReducer } from 'react';

type Patch<S> = Partial<S> | ((prev: S) => Partial<S>);

export type SetState<S> = (patch: Patch<S>) => void;

function mergeReducer<S extends object>(state: S, patch: Patch<S>): S {
  return { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
}

function identity<T>(value: T): T {
  return value;
}

/**
 * One state object, updated by shallow-merging a patch (the class-component
 * `setState` shape). Use it instead of several `useState` calls — see
 * docs/architecture/frontend.md. The setter is stable across renders; pass an updater function
 * when the patch depends on the current state.
 *
 * `init` is forwarded to `useReducer`'s third argument: the state is
 * `init(initialArg)`, computed once on mount.
 */
export function useStateReducer<S extends object>(
  initialState: S,
): [S, SetState<S>];
export function useStateReducer<S extends object, I>(
  initialArg: I,
  init: (arg: I) => S,
): [S, SetState<S>];
export function useStateReducer<S extends object, I>(
  initialArg: I,
  init?: (arg: I) => S,
): [S, SetState<S>] {
  return useReducer(
    mergeReducer<S>,
    initialArg,
    (init ?? identity) as (arg: I) => S,
  );
}
